import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { MutualFund } from '@/types/mutualFund';
import { computeMutualFundMetrics } from '@/types/mutualFund';

const RISK_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800',
};

const CATEGORY_LABELS: Record<string, string> = {
  equity: 'Equity',
  debt: 'Debt',
  hybrid: 'Hybrid',
  index: 'Index',
  sectoral: 'Sectoral',
};

interface MutualFundCardProps {
  fund: MutualFund;
  currency: string;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * MutualFundCard displays a mutual fund investment with NAV, units held,
 * unrealized gain/loss, and return percentage.
 */
export function MutualFundCard({ fund, currency, onEdit, onDelete }: MutualFundCardProps) {
  const navigate = useNavigate();
  const meta = fund.mutual_fund_metadata;

  const formatCurrency = (amount: number, curr: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: curr }).format(amount);

  const formatUnits = (units: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(units);

  const displayBalance =
    fund.convertedBalance !== undefined && fund.currency !== currency
      ? fund.convertedBalance
      : fund.balance;
  const displayCurrency =
    fund.convertedBalance !== undefined && fund.currency !== currency
      ? currency
      : fund.currency;

  const metrics = meta ? computeMutualFundMetrics(meta) : null;
  const isPositive = metrics ? metrics.unrealizedGain >= 0 : null;

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/investments/mutual-funds/${fund.id}`);
  };

  return (
    <Card
      className="w-full cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e as any);
        }
      }}
      aria-label={`View ${fund.fund_name} mutual fund details`}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1 min-w-0">
          <CardTitle className="text-base font-semibold truncate">{fund.fund_name}</CardTitle>
          <p className="text-sm text-muted-foreground">{fund.fund_manager}</p>
        </div>
        <div className="flex flex-col items-end gap-1 ml-2 shrink-0">
          {meta && (
            <>
              <Badge variant="outline" className="text-xs">
                {CATEGORY_LABELS[meta.fund_category] ?? meta.fund_category}
              </Badge>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${RISK_COLORS[meta.risk_rating] ?? ''}`}
              >
                {meta.risk_rating.charAt(0).toUpperCase() + meta.risk_rating.slice(1)} risk
              </span>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Balance */}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Current Value</span>
          <span className="font-semibold">{formatCurrency(displayBalance, displayCurrency)}</span>
        </div>

        {meta && (
          <>
            {/* Units & NAV */}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Units Held</span>
              <span className="font-medium">{formatUnits(meta.units_held)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">NAV per Unit</span>
              <span className="font-medium">
                {formatCurrency(meta.current_nav, fund.currency)}
                {meta.nav_date && (
                  <span className="text-xs text-muted-foreground ml-1">
                    ({new Date(meta.nav_date).toLocaleDateString()})
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Avg. Cost / Unit</span>
              <span className="font-medium">{formatCurrency(meta.unit_cost_basis, fund.currency)}</span>
            </div>

            {/* Gain / Loss */}
            {metrics && (
              <div className="flex justify-between text-sm border-t pt-2">
                <span className="text-muted-foreground">Unrealized P&L</span>
                <span
                  className={`font-semibold flex items-center gap-1 ${
                    isPositive ? 'text-green-600' : metrics.unrealizedGain === 0 ? 'text-muted-foreground' : 'text-red-600'
                  }`}
                >
                  {isPositive === true && <TrendingUp className="h-3 w-3" aria-hidden="true" />}
                  {isPositive === false && <TrendingDown className="h-3 w-3" aria-hidden="true" />}
                  {isPositive === null && <Minus className="h-3 w-3" aria-hidden="true" />}
                  {formatCurrency(metrics.unrealizedGain, fund.currency)}
                  <span className="text-xs font-normal">
                    ({metrics.returnPercent >= 0 ? '+' : ''}
                    {metrics.returnPercent.toFixed(2)}%)
                  </span>
                </span>
              </div>
            )}

            {meta.benchmark_index && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Benchmark</span>
                <span className="font-medium text-xs">{meta.benchmark_index}</span>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="flex-1"
            aria-label={`Edit ${fund.fund_name}`}
          >
            <Edit className="h-4 w-4 mr-1" aria-hidden="true" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="flex-1 text-destructive hover:text-destructive"
            aria-label={`Delete ${fund.fund_name}`}
          >
            <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
